import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { 
  Users, Search, Upload, Plus, ChevronLeft, ChevronRight, 
  Loader2, MoreVertical, X, Mail, Phone, ShieldAlert, Sparkles 
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Customers() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [importing, setImporting] = useState(false);
  
  const [newCustomer, setNewCustomer] = useState({
    company_name: '',
    name: '',
    role: 'PIC',
    status: 'Customer',
    segment: 'Default',
    phone: '',
    email: '',
    issue: ''
  });

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/support/customers?page=${currentPage}&limit=10&search=${searchQuery}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCustomers(data.customers || []);
        setTotalCount(data.total || 0);
        setTotalPages(data.total_pages || 1);
      } else {
        toast.error('Failed to load customers');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, [currentPage, searchQuery, token]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API}/api/support/customers/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Customers imported successfully');
        setCurrentPage(1);
        fetchCustomers();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Import failed');
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Import failed');
    } finally {
      setImporting(false);
      // Clear input
      e.target.value = '';
    }
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    if (!newCustomer.name || !newCustomer.phone) {
      toast.error('Name and Mobile fields are required');
      return;
    }

    try {
      const response = await fetch(`${API}/api/support/customers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newCustomer)
      });

      if (response.ok) {
        toast.success('Customer added successfully');
        setShowAddModal(false);
        setNewCustomer({
          company_name: '',
          name: '',
          role: 'PIC',
          status: 'Customer',
          segment: 'Default',
          phone: '',
          email: '',
          issue: ''
        });
        fetchCustomers();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to add customer');
      }
    } catch (error) {
      console.error('Add customer error:', error);
      toast.error('Failed to add customer');
    }
  };

  // Avatar helper
  const getAvatarColor = (name) => {
    const colors = [
      'bg-amber-500 text-white',
      'bg-emerald-500 text-white',
      'bg-violet-500 text-white',
      'bg-blue-500 text-white',
      'bg-rose-500 text-white',
      'bg-orange-500 text-white'
    ];
    let sum = 0;
    for (let i = 0; i < name.length; i++) {
      sum += name.charCodeAt(i);
    }
    return colors[sum % colors.length];
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="w-6 h-6 text-primary" />
            Customers
          </h1>
          <p className="text-muted-foreground">{totalCount} active customers in your database</p>
        </div>
        <div className="flex gap-2">
          {/* Import Excel */}
          <input
            type="file"
            id="excel-import"
            accept=".xlsx,.csv"
            onChange={handleFileUpload}
            className="hidden"
          />
          <label
            htmlFor="excel-import"
            className="support-btn-secondary flex items-center gap-2 cursor-pointer"
          >
            {importing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            Import Excel
          </label>

          {/* Add Customer */}
          <button
            onClick={() => setShowAddModal(true)}
            className="support-btn-primary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Customer
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-xl">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search customers..."
          value={searchQuery}
          onChange={handleSearchChange}
          className="support-input pl-11 text-base"
        />
      </div>

      {/* Table Card */}
      <div className="support-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border text-xs text-muted-foreground uppercase">
                <th className="text-left py-4 px-6 font-semibold">Company Name</th>
                <th className="text-left py-4 px-6 font-semibold">PIC</th>
                <th className="text-left py-4 px-6 font-semibold">Role</th>
                <th className="text-left py-4 px-6 font-semibold">Status</th>
                <th className="text-left py-4 px-6 font-semibold">Customer Segment</th>
                <th className="text-left py-4 px-6 font-semibold">Mobile</th>
                <th className="text-left py-4 px-6 font-semibold">Email</th>
                <th className="text-left py-4 px-6 font-semibold w-16"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/40">
              {loading ? (
                <tr>
                  <td colSpan="8" className="py-12 text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
                    <p className="text-sm text-muted-foreground mt-2">Loading customers...</p>
                  </td>
                </tr>
              ) : customers.length > 0 ? (
                customers.map((cust) => (
                  <tr key={cust.id} className="hover:bg-secondary/20 transition-colors group">
                    {/* Company Name */}
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center font-bold text-sm shrink-0 ${getAvatarColor(cust.company_name)}`}>
                          {cust.company_name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <button
                            onClick={() => navigate(`/customers/${cust.id}`)}
                            className="font-semibold text-sm hover:text-primary transition-colors text-left"
                          >
                            {cust.company_name}
                          </button>
                          <p className="text-xs text-muted-foreground">Healthcare - Malaysia</p>
                        </div>
                      </div>
                    </td>

                    {/* PIC / Doctor */}
                    <td className="py-4 px-6 text-sm font-semibold text-foreground">
                      {cust.name || '-'}
                    </td>

                    {/* Role */}
                    <td className="py-4 px-6">
                      <span className="bg-primary/20 text-primary border border-primary/30 text-3xs px-2 py-0.5 rounded font-bold uppercase">
                        {cust.role}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="py-4 px-6">
                      <span className="bg-blue-500/10 text-blue-400 border border-blue-500/20 text-3xs px-2.5 py-0.5 rounded-full font-semibold">
                        {cust.status}
                      </span>
                    </td>

                    {/* Customer Segment */}
                    <td className="py-4 px-6">
                      <span className={`text-3xs px-2.5 py-0.5 rounded-full font-semibold border ${
                        cust.segment === 'VIP' 
                          ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' 
                          : cust.segment === 'Enterprise'
                            ? 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                            : cust.segment === 'SME'
                              ? 'bg-teal-500/10 text-teal-400 border-teal-500/20'
                              : 'bg-secondary text-muted-foreground border-border/40'
                      }`}>
                        {cust.segment || 'Default'}
                      </span>
                    </td>

                    {/* Mobile */}
                    <td className="py-4 px-6 text-xs text-muted-foreground font-mono">
                      {cust.phone}
                    </td>

                    {/* Email */}
                    <td className="py-4 px-6 text-xs text-muted-foreground">
                      {cust.email}
                    </td>

                    {/* Actions */}
                    <td className="py-4 px-6">
                      <button 
                        onClick={() => navigate(`/customers/${cust.id}`)}
                        className="p-1.5 hover:bg-secondary rounded-lg text-muted-foreground hover:text-foreground"
                        title="View details"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="py-12 text-center text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-40" />
                    No customers found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-border bg-card">
            <span className="text-xs text-muted-foreground">
              Showing page {currentPage} of {totalPages}
            </span>
            <div className="flex items-center gap-1">
              <button
                disabled={currentPage === 1 || loading}
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                className="p-1.5 hover:bg-secondary rounded disabled:opacity-40 disabled:hover:bg-transparent"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              
              {/* Generate Page Numbers */}
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => setCurrentPage(p)}
                  className={`w-7 h-7 text-xs font-semibold rounded transition-colors ${
                    currentPage === p 
                      ? 'bg-primary text-primary-foreground' 
                      : 'hover:bg-secondary'
                  }`}
                >
                  {p}
                </button>
              ))}

              <button
                disabled={currentPage === totalPages || loading}
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                className="p-1.5 hover:bg-secondary rounded disabled:opacity-40 disabled:hover:bg-transparent"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Customer Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl w-full max-w-xl p-6 animate-fade-in">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                Add New Customer
              </h2>
              <button 
                onClick={() => setShowAddModal(false)} 
                className="p-1.5 hover:bg-secondary rounded-lg"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleAddCustomer} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1.5">Company Name</label>
                  <input
                    type="text"
                    required
                    value={newCustomer.company_name}
                    onChange={(e) => setNewCustomer({ ...newCustomer, company_name: e.target.value })}
                    placeholder="e.g. Vasan Eye Care"
                    className="support-input text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1.5">PIC</label>
                  <input
                    type="text"
                    required
                    value={newCustomer.name}
                    onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                    placeholder="e.g. Mathavan"
                    className="support-input text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1.5">Role</label>
                  <select
                    value={newCustomer.role}
                    onChange={(e) => setNewCustomer({ ...newCustomer, role: e.target.value })}
                    className="support-input text-sm"
                  >
                    <option value="PIC">PIC</option>
                    <option value="Owner">Owner</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1.5">Status</label>
                  <select
                    value={newCustomer.status}
                    onChange={(e) => setNewCustomer({ ...newCustomer, status: e.target.value })}
                    className="support-input text-sm"
                  >
                    <option value="Customer">Customer</option>
                    <option value="Lead">Lead</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1.5">Customer Segment</label>
                <select
                  value={newCustomer.segment}
                  onChange={(e) => setNewCustomer({ ...newCustomer, segment: e.target.value })}
                  className="support-input text-sm mb-4"
                >
                  <option value="Enterprise">Enterprise</option>
                  <option value="SME">SME</option>
                  <option value="VIP">VIP</option>
                  <option value="Default">Default</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1.5">Mobile Number</label>
                  <input
                    type="text"
                    required
                    value={newCustomer.phone}
                    onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                    placeholder="e.g. 919600388948"
                    className="support-input text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1.5">Email Address</label>
                  <input
                    type="email"
                    value={newCustomer.email}
                    onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                    placeholder="e.g. support@vasan.com"
                    className="support-input text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase mb-1.5">Initial Issue / Requirement</label>
                <textarea
                  value={newCustomer.issue}
                  onChange={(e) => setNewCustomer({ ...newCustomer, issue: e.target.value })}
                  placeholder="Describe the initial issue or service requested..."
                  rows="3"
                  className="support-input text-sm resize-none"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="support-btn-secondary px-4 text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="support-btn-primary px-5 text-sm"
                >
                  Add Customer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
